import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import TelegramLoginButton from '../components/sentry/TelegramLoginButton';
import { supabase, TABLES, type SolanaTokenData } from '../utils/supabase';
import { fetchSentryFeeSwapSummary, type SentryFeeSwapSummary } from '../utils/sentryBotApi';
import {
  fetchTelegramMe,
  loginTelegram,
  logoutTelegram,
  type TelegramAuthPayload,
  type TelegramUser
} from '../utils/telegramAuth';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useRealtimePrices, formatPriceChange } from '../hooks/useRealtimePrices';
import SolanaTradeModal from '../components/shared/modals/SolanaTradeModal';
import { SolanaWalletProvider } from '../components/SolanaWalletProvider';
import SentryPriceFooter from '../components/sentry/SentryPriceFooter';
import { formatPriceDisplay } from '../utils/priceFormatter';

// ============================================
// SENTRY TOKENS PAGE
// Tokens list for sentry.trading/tokens
// ============================================
const DEXSCREENER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/DEXS.png';
const ORCA_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/ORCA-BUY.png';
const WEBSITE_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/WEB.png';
const TWITTER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/X.png';
const TELEGRAM_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/TG.png';
const TELEGRAM_SYMBOL_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/Telegram%20symbol.png';
const SENTRY_LOGO_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/Sentry%20Logo.png';
const SENTRY_LAUNCH_MARKET_BUY = 19465720.490301692;
const SOLANA_CHAIN_ID = 792703809;

const PageContainer = styled.div`
  padding: 24px;
  background: #000000;
  min-height: 100vh;
  width: 100%;
  padding-bottom: 84px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 20px;
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  margin-bottom: 24px;
  min-height: 56px;
  gap: 20px;
  flex-wrap: wrap;
  position: relative;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
`;

const HeaderRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    justify-content: center;
    order: 2;
  }
`;

const HeaderLogo = styled.img`
  height: 32px;
  width: auto;
  object-fit: contain;
`;

const HeaderStatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
`;

const HeaderStatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
  text-align: center;
`;

const HeaderStatsDesktop = styled(HeaderStatsRow)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderStatsMobile = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    position: relative;
    justify-content: center;
    order: 1;
  }
`;

const HeaderDropdownButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
`;

const HeaderDropdownPanel = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'grid' : 'none')};
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 5;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 12px;
  background: rgba(5, 5, 8, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderStatLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const HeaderStatValue = styled.span<{ $tone?: 'green' | 'gold' }>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ $tone }) => {
    if ($tone === 'gold') return '#FFD700';
    if ($tone === 'green') return '#10b981';
    return '#ffffff';
  }};
`;

const HeaderDocsButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 2px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ffffff;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderDocsButtonMobile = styled(HeaderDocsButton)`
  display: inline-flex;

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const TelegramSessionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const TelegramActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletConnectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 28px;
  min-height: 28px;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: contain;
`;

const WalletAddress = styled.span`
  font-family: 'Inter', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
`;

const PageHeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const CreateTokenButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(135deg, #14F195 0%, #10b981 100%);
  color: #000000;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(20, 241, 149, 0.3);
  }
`;

const CreateTokenIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;

const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 150ms ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.18);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.04);
  }
`;

const TokensGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TokenCard = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const TokenHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
`;

const TokenHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const TokenLogo = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  background: #000000;
  border: 1px solid #333333;
  flex-shrink: 0;
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TokenName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const TokenAddress = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-family: monospace;
`;

const TokenLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TokenStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const TokenStatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TokenStatLabel = styled.span`
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 10px;
`;

const TokenStatValue = styled.span<{ $tone?: 'green' | 'red' }>`
  color: ${({ $tone }) => {
    if ($tone === 'green') return '#10b981';
    if ($tone === 'red') return '#ef4444';
    return '#ffffff';
  }};
  font-weight: 600;
`;

const TradeButton = styled.button<{ $disabled?: boolean }>`
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #14F195 0%, #10b981 100%)')};
  color: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.4)' : '#000000')};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    ${({ $disabled }) => (!$disabled ? 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(20, 241, 149, 0.3);' : '')}
  }
`;

const TokenLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const TokenLinkIcon = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
`;

const ensureHttpsUrl = (url: string | null | undefined): string => {
  if (!url) return '/token-placeholder.png';
  if (url.startsWith('Qm') && url.length === 46) {
    return `https://ipfs.io/ipfs/${url}`;
  }
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const normalizeWebsiteUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://${url}`;
};

const normalizeTwitterUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://twitter.com/${url.replace('@', '')}`;
};

const normalizeTelegramUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://t.me/${url.replace('@', '')}`;
};

const formatTokenAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '--';
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: amount < 1 ? 4 : 2,
    maximumFractionDigits: amount < 1 ? 6 : 2
  });
};

const formatUsdAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '--';
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatMarketCapValue = (value: number): string => {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const SentryTokensPageContent: React.FC = () => {
  const { connected, publicKey, wallet } = useWallet();
  const { setVisible } = useWalletModal();
  const [tokens, setTokens] = useState<SolanaTokenData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [feeSwapSummary, setFeeSwapSummary] = useState<SentryFeeSwapSummary | null>(null);
  const [feeSwapSummaryError, setFeeSwapSummaryError] = useState<string | null>(null);
  const [buybacksOpen, setBuybacksOpen] = useState(false);
  const [telegramUser, setTelegramUser] = useState<TelegramUser | null>(null);
  const [telegramAuthError, setTelegramAuthError] = useState<string | null>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<{
    address: string;
    symbol: string;
    name: string;
    logo?: string | null;
    decimals?: number;
    poolAddress?: string;
    tokenVaultA?: string;
    tokenVaultB?: string;
    tickSpacing?: number;
  } | null>(null);
  const { getPriceForToken, get24hChangeForToken, loading: pricesLoading } = useRealtimePrices(SOLANA_CHAIN_ID);

  const sentryBuyBacks = feeSwapSummary
    ? Math.max(0, feeSwapSummary.totalSentryBought - SENTRY_LAUNCH_MARKET_BUY)
    : null;
  const sentryBuyBacksUsd = sentryBuyBacks !== null && feeSwapSummary
    ? sentryBuyBacks * feeSwapSummary.sentryPriceUsd
    : null;
  const headerFeeStats = [
    {
      label: 'Sentry Bot Trades',
      value: feeSwapSummary ? feeSwapSummary.totalBuys.toLocaleString('en-US') : '--',
      tone: 'green' as const
    },
    {
      label: '$SENTRY Buy Backs',
      value: formatTokenAmount(sentryBuyBacks),
      tone: 'gold' as const
    },
    {
      label: 'USD Value',
      value: formatUsdAmount(sentryBuyBacksUsd),
      tone: 'green' as const
    }
  ];
  const telegramBotUsername = (import.meta.env as any).VITE_TELEGRAM_BOT_USERNAME as string | undefined;

  const handleTelegramAuth = async (payload: TelegramAuthPayload) => {
    setTelegramLoading(true);
    setTelegramAuthError(null);
    try {
      const user = await loginTelegram(payload);
      setTelegramUser(user);
    } catch (authError: any) {
      setTelegramAuthError(authError?.message || 'Telegram login failed.');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramLogout = async () => {
    setTelegramLoading(true);
    setTelegramAuthError(null);
    try {
      await logoutTelegram();
      setTelegramUser(null);
    } catch (logoutError: any) {
      setTelegramAuthError(logoutError?.message || 'Failed to disconnect Telegram.');
    } finally {
      setTelegramLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadTelegramUser = async () => {
      try {
        const user = await fetchTelegramMe();
        if (!cancelled) {
          setTelegramUser(user);
        }
      } catch (loadError) {
        if (!cancelled) {
          setTelegramUser(null);
        }
      }
    };

    loadTelegramUser();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const fetchSummary = async () => {
      try {
        setFeeSwapSummaryError(null);
        const summary = await fetchSentryFeeSwapSummary();
        if (cancelled) return;
        if (summary) {
          setFeeSwapSummary(summary);
        } else if (!feeSwapSummary) {
          setFeeSwapSummaryError('Swap summary unavailable');
        }
      } catch (summaryError) {
        if (!cancelled) {
          if (!feeSwapSummary) {
            setFeeSwapSummaryError('Swap summary unavailable');
          }
        }
      } finally {
        if (cancelled) return;
        const shouldPollFast = feeSwapSummary?.truncated || false;
        const nextDelayMs = shouldPollFast ? 60 * 1000 : 30 * 60 * 1000;
        timeoutId = setTimeout(fetchSummary, nextDelayMs);
      }
    };

    fetchSummary();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [feeSwapSummary?.truncated]);

  useEffect(() => {
    let cancelled = false;
    const normalizedQuery = searchQuery.trim();

    const fetchTokens = async () => {
      setLoading(true);
      setError(null);
      try {
        let query = supabase
          .from(TABLES.TOKENS_SOL)
          .select('*')
          .order('created_at', { ascending: false });

        if (normalizedQuery) {
          const safeQuery = normalizedQuery.replace(/,/g, '');
          query = query.or(
            `name.ilike.%${safeQuery}%,symbol.ilike.%${safeQuery}%,contract_address.ilike.%${safeQuery}%`
          );
        }

        const { data, error: queryError } = await query;
        if (!cancelled) {
          if (queryError) {
            setError('Failed to load tokens');
            setTokens([]);
            return;
          }
          setTokens(data || []);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError('Failed to load tokens');
          setTokens([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const debounceId = setTimeout(fetchTokens, 300);

    return () => {
      cancelled = true;
      clearTimeout(debounceId);
    };
  }, [searchQuery]);

  const handleTradeClick = (token: SolanaTokenData) => {
    const mintAddress = token.mint_address || token.contract_address;
    const poolAddress = token.pool_address;

    if (!mintAddress) {
      return;
    }

    setSelectedToken({
      address: mintAddress,
      symbol: token.symbol,
      name: token.name,
      logo: token.logo_url,
      decimals: token.decimals || 9,
      poolAddress,
      tokenVaultA: token.token_vault_a,
      tokenVaultB: token.token_vault_b,
      tickSpacing: token.tick_spacing || 128
    });
    setIsTradeModalOpen(true);
  };

  const walletLabel = connected && publicKey
    ? `${publicKey.toBase58().slice(0, 4)}...${publicKey.toBase58().slice(-4)}`
    : 'Connect Wallet';

  const walletIcon = connected ? wallet?.adapter?.icon : null;

  return (
    <PageContainer>
      <HeaderBar>
        <HeaderLeft>
          <HeaderLogo src={SENTRY_LOGO_URL} alt="Sentry" />
        </HeaderLeft>
        <HeaderStatsDesktop>
          {headerFeeStats.map((stat) => (
            <HeaderStatItem key={stat.label}>
              <HeaderStatLabel>{stat.label}</HeaderStatLabel>
              <HeaderStatValue $tone={stat.tone}>{stat.value}</HeaderStatValue>
            </HeaderStatItem>
          ))}
        </HeaderStatsDesktop>
        <HeaderDocsButton href="https://sentry.trading/dashboard">
          Dashboard
        </HeaderDocsButton>
        <HeaderDocsButton href="https://sentry.trading/tokens">
          Tokens
        </HeaderDocsButton>
        <HeaderDocsButton href="https://sentry.trading/docs" target="_blank" rel="noopener noreferrer">
          Docs
        </HeaderDocsButton>
        <HeaderStatsMobile>
          <HeaderDropdownButton onClick={() => setBuybacksOpen((prev) => !prev)}>
            Buy Backs
            <span>{buybacksOpen ? '−' : '+'}</span>
          </HeaderDropdownButton>
          <HeaderDocsButtonMobile href="https://sentry.trading/dashboard">
            Dashboard
          </HeaderDocsButtonMobile>
          <HeaderDocsButtonMobile href="https://sentry.trading/tokens">
            Tokens
          </HeaderDocsButtonMobile>
          <HeaderDocsButtonMobile href="https://sentry.trading/docs" target="_blank" rel="noopener noreferrer">
            Docs
          </HeaderDocsButtonMobile>
          <HeaderDropdownPanel $open={buybacksOpen}>
            {headerFeeStats.map((stat) => (
              <HeaderStatItem key={`mobile-${stat.label}`}>
                <HeaderStatLabel>{stat.label}</HeaderStatLabel>
                <HeaderStatValue $tone={stat.tone}>{stat.value}</HeaderStatValue>
              </HeaderStatItem>
            ))}
          </HeaderDropdownPanel>
        </HeaderStatsMobile>
        <HeaderRight>
          <WalletConnectButton type="button" onClick={() => setVisible(true)}>
            {walletIcon && <WalletIcon src={walletIcon} alt={`${wallet?.adapter?.name || 'Wallet'} logo`} />}
            {connected ? <WalletAddress>{walletLabel}</WalletAddress> : walletLabel}
          </WalletConnectButton>
          {telegramUser ? (
            <>
              <TelegramSessionBadge>
                Connected{telegramUser.username ? ` @${telegramUser.username}` : ''}
              </TelegramSessionBadge>
              <TelegramActionButton onClick={handleTelegramLogout} disabled={telegramLoading}>
                Disconnect
              </TelegramActionButton>
            </>
          ) : (
            <TelegramLoginButton botUsername={telegramBotUsername} onAuth={handleTelegramAuth} />
          )}
        </HeaderRight>
      </HeaderBar>
      {telegramAuthError && <EmptyState>{telegramAuthError}</EmptyState>}
      {feeSwapSummaryError && <EmptyState>{feeSwapSummaryError}</EmptyState>}

      <PageHeaderRow>
        <PageHeaderText>
          <PageTitle>Solana Tokens</PageTitle>
          <PageSubtitle>Browse tokens deployed on Sentry Trading</PageSubtitle>
        </PageHeaderText>
        <CreateTokenButton
          href="https://t.me/sentry_trading_bot"
          target="_blank"
          rel="noopener noreferrer"
        >
          Create a Token
          <CreateTokenIcon src={TELEGRAM_SYMBOL_ICON_URL} alt="Telegram" />
        </CreateTokenButton>
      </PageHeaderRow>

      <SearchSection>
        <SearchInput
          type="text"
          placeholder="Search by name, symbol, or address..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
      </SearchSection>

      {loading ? (
        <EmptyState>Loading tokens...</EmptyState>
      ) : error ? (
        <EmptyState>{error}</EmptyState>
      ) : tokens.length === 0 ? (
        <EmptyState>No tokens found</EmptyState>
      ) : (
        <TokensGrid>
          {tokens.map((token) => {
            const tokenAddress = token.contract_address || token.mint_address;
            const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
            const change24h = tokenAddress ? get24hChangeForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
            const marketCap = priceUSD ? formatMarketCapValue(priceUSD * 1_000_000_000) : '--';
            const websiteUrl = normalizeWebsiteUrl(token.website);
            const twitterUrl = normalizeTwitterUrl(token.twitter);
            const telegramUrl = normalizeTelegramUrl(token.telegram);
            const dexscreenerUrl = tokenAddress ? `https://dexscreener.com/solana/${tokenAddress}` : null;
            const orcaUrl = tokenAddress
              ? `https://www.orca.so/?tokenIn=So11111111111111111111111111111111111111112&tokenOut=${encodeURIComponent(tokenAddress)}`
              : null;

            return (
              <TokenCard key={token.id || tokenAddress}>
                <TokenHeader>
                  <TokenHeaderLeft>
                    <TokenLogo
                      src={ensureHttpsUrl(token.logo_url)}
                      alt={token.name}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        (e.target as HTMLImageElement).src = '/token-placeholder.png';
                      }}
                    />
                    <TokenInfo>
                      <TokenName>{token.name}</TokenName>
                      <TokenSymbol>${token.symbol}</TokenSymbol>
                    </TokenInfo>
                  </TokenHeaderLeft>
                  <TradeButton
                    onClick={() => handleTradeClick(token)}
                    $disabled={!token.pool_address}
                    disabled={!token.pool_address}
                  >
                    {token.pool_address ? 'Trade' : 'No Pool'}
                  </TradeButton>
                </TokenHeader>
                {tokenAddress && (
                  <TokenAddress>
                    {tokenAddress.slice(0, 8)}...{tokenAddress.slice(-6)}
                  </TokenAddress>
                )}
                <TokenStats>
                  <TokenStatRow>
                    <TokenStatLabel>Market Cap</TokenStatLabel>
                    <TokenStatValue>{pricesLoading ? '--' : marketCap}</TokenStatValue>
                  </TokenStatRow>
                  <TokenStatRow>
                    <TokenStatLabel>Price</TokenStatLabel>
                    <TokenStatValue>{pricesLoading ? '--' : formatPriceDisplay(priceUSD)}</TokenStatValue>
                  </TokenStatRow>
                  {change24h !== null && !pricesLoading && (
                    <TokenStatRow>
                      <TokenStatLabel>24h</TokenStatLabel>
                      <TokenStatValue $tone={change24h >= 0 ? 'green' : 'red'}>
                        {formatPriceChange(change24h)}
                      </TokenStatValue>
                    </TokenStatRow>
                  )}
                </TokenStats>
                <TokenLinks>
                  {websiteUrl && (
                    <TokenLink href={websiteUrl} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={WEBSITE_ICON_URL} alt="Website" />
                    </TokenLink>
                  )}
                  {dexscreenerUrl && (
                    <TokenLink href={dexscreenerUrl} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={DEXSCREENER_ICON_URL} alt="Dexscreener" />
                    </TokenLink>
                  )}
                  {orcaUrl && (
                    <TokenLink href={orcaUrl} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={ORCA_ICON_URL} alt="Orca buy" />
                    </TokenLink>
                  )}
                  {twitterUrl && (
                    <TokenLink href={twitterUrl} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={TWITTER_ICON_URL} alt="Twitter" />
                    </TokenLink>
                  )}
                  {telegramUrl && (
                    <TokenLink href={telegramUrl} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={TELEGRAM_ICON_URL} alt="Telegram" />
                    </TokenLink>
                  )}
                </TokenLinks>
              </TokenCard>
            );
          })}
        </TokensGrid>
      )}
      <SolanaTradeModal
        isOpen={isTradeModalOpen}
        onClose={() => {
          setIsTradeModalOpen(false);
          setSelectedToken(null);
        }}
        token={selectedToken}
      />
      <SentryPriceFooter />
    </PageContainer>
  );
};

const SentryTokensPage: React.FC = () => (
  <SolanaWalletProvider>
    <SentryTokensPageContent />
  </SolanaWalletProvider>
);

export default SentryTokensPage;
